/**
 * DeleteAccountEntry — the "Danger zone" entry point.
 *
 * Sits at the BOTTOM of Manage Subscription, and — unchanged — at the
 * bottom of the Parental Controls "Data & privacy" tab. Same component,
 * two homes (the brief's "SAME modal reuse").
 *
 * Placement decisions (the brief left them to us):
 *   - Fenced-off "Danger zone" section, terracotta `danger`.
 *   - Trigger is a restrained text link, not a big red button — can't
 *     be hit by reflex; the password challenge inside is the friction.
 *   - When deletion is already scheduled, the same slot flips to show
 *     the date + a one-tap "Cancel deletion", so the parent always has
 *     a way back.
 *
 * Self-contained: renders its own DeleteConfirmModal and reads the
 * erasure store directly. Consumers pass in the family summary so the
 * confirm modal can show real kid names + plan label.
 */

import { useState } from "react";
import { Icon } from "../chrome/Icon";
import { ChromeTokens } from "../chrome/tokens";
import { DeleteConfirmModal } from "./DeleteConfirmModal";
import { type FamilySummary } from "./erasureCategories";
import { GRACE_DAYS, useErasure } from "./useErasure";

interface Props {
  family: FamilySummary;
  /** Visual context. Identical behaviour either way. */
  context?: "manage" | "parental";
}

const tokens = ChromeTokens;

export function DeleteAccountEntry({ family, context = "manage" }: Props): JSX.Element {
  const e = useErasure();
  const [open, setOpen] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [cancelErr, setCancelErr] = useState<string | null>(null);

  const handleCancel = async () => {
    setCancelBusy(true);
    setCancelErr(null);
    try {
      await e.cancel();
    } catch (err) {
      const name = (err as Error).name;
      setCancelErr(
        name === "GraceAlreadyElapsedError"
          ? "The deletion already ran — there's nothing left to cancel."
          : "Couldn't cancel. Please try again.",
      );
    } finally {
      setCancelBusy(false);
    }
  };

  return (
    <div
      data-context={context}
      style={{
        marginTop: tokens.spacing.sm,
        borderTop: `1px solid ${tokens.color.baseMuted}`,
        paddingTop: tokens.spacing.lg,
      }}
    >
      <div style={{ display: "inline-flex", alignItems: "center", gap: 7, marginBottom: tokens.spacing.sm }}>
        <Icon name="alert-triangle" size={13} stroke={tokens.color.danger} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: ".07em",
            textTransform: "uppercase",
            color: tokens.color.danger,
          }}
        >
          Danger zone
        </span>
      </div>

      <div
        style={{
          border: `1.5px solid color-mix(in srgb, ${tokens.color.danger} 26%, ${tokens.color.baseMuted})`,
          borderRadius: tokens.radius.md,
          padding: "13px 15px",
          background: `color-mix(in srgb, ${tokens.color.danger} 7%, ${tokens.color.base})`,
        }}
      >
        {e.pending
          ? (
            <div style={{ display: "flex", alignItems: "center", gap: tokens.spacing.md, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3, color: tokens.color.ink }}>
                  Deletion scheduled
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: tokens.color.inkMuted,
                    lineHeight: 1.45,
                    marginTop: 2,
                  }}
                >
                  Your family account is set to be erased on <strong>{e.scheduledStr ?? "the scheduled date"}</strong>
                  {" · "}
                  {e.daysLeft} day{e.daysLeft === 1 ? "" : "s"} left.
                </div>
                {cancelErr && (
                  <div style={{ fontSize: 12, color: tokens.color.danger, marginTop: 4 }}>{cancelErr}</div>
                )}
              </div>
              <button
                type="button"
                disabled={cancelBusy}
                onClick={handleCancel}
                style={{
                  flexShrink: 0,
                  background: tokens.color.base,
                  border: `1px solid ${tokens.color.baseMuted}`,
                  borderRadius: tokens.radius.pill,
                  padding: "7px 14px",
                  fontFamily: tokens.font.ui,
                  fontWeight: 800,
                  fontSize: 13,
                  color: tokens.color.ink,
                  cursor: cancelBusy ? "wait" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Icon name="rotate-ccw" size={14} stroke={tokens.color.ink} />
                {cancelBusy ? "Cancelling…" : "Cancel deletion"}
              </button>
            </div>
          )
          : (
            <div style={{ display: "flex", alignItems: "flex-start", gap: tokens.spacing.md, flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 180 }}>
                <div style={{ fontSize: 14, fontWeight: 800, lineHeight: 1.3, color: tokens.color.ink }}>
                  Delete my family account
                </div>
                <div
                  style={{
                    fontSize: 12.5,
                    color: tokens.color.inkMuted,
                    lineHeight: 1.45,
                    marginTop: 2,
                  }}
                >
                  Permanently remove your account and everyone's data — profiles,
                  worlds, conversations and avatars. You'll have {GRACE_DAYS} days
                  to undo it.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(true)}
                style={{
                  flexShrink: 0,
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  padding: "4px 2px",
                  color: tokens.color.danger,
                  fontWeight: 800,
                  fontSize: 13.5,
                  fontFamily: tokens.font.ui,
                  textDecoration: "underline",
                  textUnderlineOffset: 3,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                <Icon name="trash-2" size={15} stroke={tokens.color.danger} />
                Delete…
              </button>
            </div>
          )}
      </div>

      <DeleteConfirmModal open={open} onClose={() => setOpen(false)} family={family} />
    </div>
  );
}
